<template>
  <div class="container-fluid">
    <div class="row mt-3">
      <div class="col-md-8 col-lg-7 mx-auto">

        <!-- ── Paso 1: Seleccionar Programa ── -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">
              <span class="badge bg-primary me-2">1</span>
              Selecciona un programa
            </h6>
          </div>
          <div class="card-body">

            <div v-if="loadingProgramas" class="text-center py-3">
              <div class="loader-box"><div class="loader-3"></div></div>
            </div>
            <div v-else-if="errorProgramas" class="alert alert-danger py-2 small">
              {{ errorProgramas }}
              <button class="btn btn-sm btn-link p-0 ms-2" @click="fetchProgramas">Reintentar</button>
            </div>
            <div v-else>
              <select v-model="programaSeleccionado" class="form-select" @change="onProgramaChange">
                <option :value="null" disabled>— Elige un programa —</option>
                <option v-for="p in programas" :key="p.id" :value="p">{{ p.nombre }}</option>
              </select>
            </div>

          </div>
        </div>

        <!-- ── Paso 2: Seleccionar Métrica ── -->
        <div class="card mb-3" :class="{ 'opacity-50 pe-none': !programaSeleccionado }">
          <div class="card-header">
            <h6 class="mb-0">
              <span class="badge bg-primary me-2">2</span>
              Selecciona una métrica
            </h6>
          </div>
          <div class="card-body">

            <div v-if="loadingMetricas" class="text-center py-3">
              <div class="loader-box"><div class="loader-3"></div></div>
            </div>
            <div v-else-if="errorMetricas" class="alert alert-danger py-2 small">
              {{ errorMetricas }}
              <button class="btn btn-sm btn-link p-0 ms-2" @click="fetchMetricas">Reintentar</button>
            </div>
            <div v-else>
              <select v-model="metricaSeleccionada" class="form-select" :disabled="!programaSeleccionado">
                <option :value="null" disabled>— Elige una métrica —</option>
                <option v-for="m in metricas" :key="m.id" :value="m">{{ m.nombre_metrica }}</option>
              </select>
              <div v-if="metricaSeleccionada" class="mt-2 p-2 bg-light rounded small text-muted">
                <strong>Definición:</strong> {{ metricaSeleccionada.definicion }}<br />
                <strong>Fórmula:</strong> {{ metricaSeleccionada.formula }}<br />
                <strong>Unidad:</strong> {{ metricaSeleccionada.unidad_medida }} ·
                <strong>Periodicidad:</strong> {{ metricaSeleccionada.periodicidad }}
              </div>
            </div>

          </div>
        </div>

        <!-- ── Paso 3: Registrar valor ── -->
        <div class="card" :class="{ 'opacity-50 pe-none': !metricaSeleccionada }">
          <div class="card-header">
            <h6 class="mb-0">
              <span class="badge bg-primary me-2">3</span>
              Registra el valor
            </h6>
          </div>
          <div class="card-body">

            <div v-if="successMsg" class="alert alert-success d-flex align-items-center">
              <vue-feather type="check-circle" size="16" class="me-2"></vue-feather> {{ successMsg }}
            </div>
            <div v-if="errorMsg" class="alert alert-danger d-flex align-items-center">
              <vue-feather type="alert-circle" size="16" class="me-2"></vue-feather> {{ errorMsg }}
            </div>

            <form @submit.prevent="guardar" novalidate>
              <div class="row g-3">

                <div class="col-md-6">
                  <label class="form-label">Fecha de corte <span class="text-danger">*</span></label>
                  <input
                    v-model="form.fecha_corte"
                    type="date"
                    class="form-control"
                    :class="{ 'is-invalid': errors.fecha_corte }"
                    :disabled="!metricaSeleccionada"
                  />
                  <div class="invalid-feedback">Campo requerido.</div>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Fecha de registro <span class="text-danger">*</span></label>
                  <input
                    v-model="form.fecha_registro"
                    type="date"
                    class="form-control"
                    :class="{ 'is-invalid': errors.fecha_registro }"
                    :disabled="!metricaSeleccionada"
                  />
                  <div class="invalid-feedback">Campo requerido.</div>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Valor programado <span class="text-danger">*</span></label>
                  <div v-if="metricaSeleccionada?.unidad_medida === 'MONETARIO'" class="input-group" :class="{ 'is-invalid': errors.valor_programado }">
                    <span class="input-group-text">$</span>
                    <input
                      v-model.number="form.valor_programado"
                      type="number"
                      step="any"
                      class="form-control"
                      :class="{ 'is-invalid': errors.valor_programado }"
                      placeholder="0"
                      :disabled="!metricaSeleccionada"
                    />
                  </div>
                  <input
                    v-else
                    v-model.number="form.valor_programado"
                    type="number"
                    step="any"
                    class="form-control"
                    :class="{ 'is-invalid': errors.valor_programado }"
                    placeholder="0"
                    :disabled="!metricaSeleccionada"
                  />
                  <div class="invalid-feedback">Campo requerido.</div>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Valor real <span class="text-danger">*</span></label>
                  <div v-if="metricaSeleccionada?.unidad_medida === 'MONETARIO'" class="input-group" :class="{ 'is-invalid': errors.valor_real }">
                    <span class="input-group-text">$</span>
                    <input
                      v-model.number="form.valor_real"
                      type="number"
                      step="any"
                      class="form-control"
                      :class="{ 'is-invalid': errors.valor_real }"
                      placeholder="0"
                      :disabled="!metricaSeleccionada"
                    />
                  </div>
                  <input
                    v-else
                    v-model.number="form.valor_real"
                    type="number"
                    step="any"
                    class="form-control"
                    :class="{ 'is-invalid': errors.valor_real }"
                    placeholder="0"
                    :disabled="!metricaSeleccionada"
                  />
                  <div class="invalid-feedback">Campo requerido.</div>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Presupuesto ejercido</label>
                  <input
                    v-model.number="form.presupuesto_ejercido"
                    type="number"
                    step="any"
                    class="form-control"
                    placeholder="Opcional"
                    :disabled="!metricaSeleccionada"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label">Notas de desviación</label>
                  <textarea
                    v-model="form.notas_desviacion"
                    class="form-control"
                    rows="2"
                    placeholder="Describe cualquier desviación respecto a lo programado…"
                    :disabled="!metricaSeleccionada"
                  ></textarea>
                </div>

              </div>

              <div class="d-flex justify-content-end gap-2 mt-4">
                <button type="button" class="btn btn-outline-secondary" @click="resetear">Limpiar</button>
                <button type="submit" class="btn btn-primary" :disabled="guardando || !metricaSeleccionada">
                  <span v-if="guardando">
                    <span class="spinner-border spinner-border-sm me-1"></span> Guardando…
                  </span>
                  <span v-else>
                    <vue-feather type="save" size="14" class="me-1"></vue-feather> Registrar valor
                  </span>
                </button>
              </div>
            </form>

          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axios";
import authService from "@/services/authService";

const today = () => new Date().toISOString().slice(0, 10);

const FORM_VACIO = () => ({
  fecha_corte: today(),
  valor_programado: null,
  valor_real: null,
  presupuesto_ejercido: null,
  notas_desviacion: "",
  fecha_registro: today(),
});

export default {
  name: "EvaluacionesRegistro",

  data() {
    return {
      // Paso 1
      programas: [],
      loadingProgramas: false,
      errorProgramas: null,
      programaSeleccionado: null,

      // Paso 2
      metricas: [],
      loadingMetricas: false,
      errorMetricas: null,
      metricaSeleccionada: null,

      // Paso 3
      form: FORM_VACIO(),
      errors: {},
      guardando: false,
      successMsg: null,
      errorMsg: null,
    };
  },

  created() {
    this.fetchProgramas();
  },

  methods: {
    async fetchProgramas() {
      this.loadingProgramas = true;
      this.errorProgramas = null;
      try {
        const ud = authService.getUserData();
        const userId = ud?.id ?? ud?.pk;
        const { data } = await api.get(`usuarios/${userId}/programas/`);
        this.programas = Array.isArray(data) ? data : (data.programas ?? data.results ?? []);
      } catch (err) {
        this.errorProgramas = err.response?.data?.detail ?? "Error al cargar los programas.";
      } finally {
        this.loadingProgramas = false;
      }
    },

    onProgramaChange() {
      this.metricaSeleccionada = null;
      this.metricas = [];
      this.errors = {};
      this.successMsg = null;
      this.errorMsg = null;
      if (this.programaSeleccionado) {
        this.fetchMetricas();
      }
    },

    async fetchMetricas() {
      this.loadingMetricas = true;
      this.errorMetricas = null;
      try {
        const { data } = await api.get(`metricas-config/${this.programaSeleccionado.id}`);
        this.metricas = Array.isArray(data) ? data : (data.results ?? []);
      } catch (err) {
        this.errorMetricas = err.response?.data?.detail ?? "Error al cargar las métricas.";
      } finally {
        this.loadingMetricas = false;
      }
    },

    validar() {
      const e = {};
      if (!this.form.fecha_corte) e.fecha_corte = true;
      if (!this.form.fecha_registro) e.fecha_registro = true;
      if (this.form.valor_programado === null || this.form.valor_programado === "") e.valor_programado = true;
      if (this.form.valor_real === null || this.form.valor_real === "") e.valor_real = true;
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    async guardar() {
      if (!this.validar()) return;
      this.guardando = true;
      this.successMsg = null;
      this.errorMsg = null;
      try {
        const payload = {
          metrica: this.metricaSeleccionada.id,
          fecha_corte: this.form.fecha_corte,
          valor_programado: this.form.valor_programado,
          valor_real: this.form.valor_real,
          presupuesto_ejercido: this.form.presupuesto_ejercido !== "" ? this.form.presupuesto_ejercido : null,
          notas_desviacion: this.form.notas_desviacion,
          fecha_registro: this.form.fecha_registro,
        };
        await api.post("metricas-historial/", payload);
        this.successMsg = "Valor registrado correctamente.";
        this.form = FORM_VACIO();
        this.errors = {};
      } catch (err) {
        this.errorMsg =
          err.response?.data?.detail ??
          JSON.stringify(err.response?.data) ??
          "Error al registrar el valor.";
      } finally {
        this.guardando = false;
      }
    },

    resetear() {
      this.form = FORM_VACIO();
      this.errors = {};
      this.successMsg = null;
      this.errorMsg = null;
      this.metricaSeleccionada = null;
      this.programaSeleccionado = null;
      this.metricas = [];
    },
  },
};
</script>

<style scoped>
.pe-none {
  pointer-events: none;
}
</style>
